/**
 * UpdatePrompt - Notificación de actualización disponible
 *
 * Muestra un banner cuando hay una nueva versión de la app disponible.
 * El usuario puede actualizar inmediatamente o ignorar.
 */

import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw, X, Sparkles } from 'lucide-react';
import { cn } from '@/core/utils';
import { useServiceWorker } from '@/core/hooks';

export function UpdatePrompt() {
  const { needRefresh, offlineReady, updateServiceWorker, close } = useServiceWorker();

  // No mostrar nada si no hay notificación
  if (!needRefresh && !offlineReady) {
    return null;
  }

  return (
    <AnimatePresence>
      {(needRefresh || offlineReady) && (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 50, scale: 0.95 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className={cn(
            'fixed bottom-24 left-4 right-4 z-[90]',
            'sm:left-auto sm:right-6 sm:w-80',
            'p-4 rounded-xl',
            'border shadow-xl',
            needRefresh
              ? 'bg-blue-900/95 border-blue-700 shadow-blue-900/30'
              : 'bg-emerald-900/95 border-emerald-700 shadow-emerald-900/30'
          )}
        >
          <div className="flex items-start gap-3">
            {/* Icono */}
            <div
              className={cn(
                'p-2 rounded-lg flex-shrink-0',
                needRefresh ? 'bg-blue-800' : 'bg-emerald-800'
              )}
            >
              {needRefresh ? (
                <Sparkles className="w-5 h-5 text-blue-300" />
              ) : (
                <RefreshCw className="w-5 h-5 text-emerald-300" />
              )}
            </div>

            {/* Contenido */}
            <div className="flex-1 min-w-0">
              <h3
                className={cn(
                  'font-semibold text-sm',
                  needRefresh ? 'text-blue-100' : 'text-emerald-100'
                )}
              >
                {needRefresh ? 'Nueva versión disponible' : 'Listo para usar offline'}
              </h3>
              <p
                className={cn(
                  'text-xs mt-1',
                  needRefresh ? 'text-blue-300' : 'text-emerald-300'
                )}
              >
                {needRefresh
                  ? 'Hay mejoras esperándote. Actualiza para obtenerlas.'
                  : 'La app está lista para funcionar sin conexión.'}
              </p>

              {/* Botones */}
              <div className="flex gap-2 mt-3">
                {needRefresh && (
                  <button
                    onClick={updateServiceWorker}
                    className={cn(
                      'flex items-center gap-1.5 px-3 py-1.5 rounded-lg',
                      'text-xs font-medium',
                      'bg-blue-600 hover:bg-blue-500 text-white',
                      'transition-colors'
                    )}
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    Actualizar
                  </button>
                )}
                <button
                  onClick={close}
                  className={cn(
                    'px-3 py-1.5 rounded-lg',
                    'text-xs font-medium',
                    needRefresh
                      ? 'text-blue-300 hover:bg-blue-800'
                      : 'text-emerald-300 hover:bg-emerald-800',
                    'transition-colors'
                  )}
                >
                  {needRefresh ? 'Después' : 'Entendido'}
                </button>
              </div>
            </div>

            {/* Cerrar */}
            <button
              onClick={close}
              className={cn(
                'p-1 rounded-lg transition-colors flex-shrink-0',
                needRefresh
                  ? 'text-blue-400 hover:bg-blue-800'
                  : 'text-emerald-400 hover:bg-emerald-800'
              )}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
